import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { WarningBadge } from "@/components/Badges";
import ClickableImage from "@/components/ClickableImage";
import { toast } from "sonner";
import { ShieldCheck, Check, X } from "lucide-react";

function Thumb({ path, label }) {
  if (!path) return null;
  return (
    <div>
      <ClickableImage path={path} label={label} className="w-full aspect-video object-cover border border-zinc-800 rounded-sm hover:border-primary transition-colors" />
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

export default function AdminReports() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/reports", { params: { status: filter } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/reports/${id}/${action}`);
      toast.success(action === "approve" ? "Đã duyệt báo cáo" : "Đã từ chối báo cáo");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const tabCls = (t) => `px-4 py-2 text-sm font-semibold rounded-sm transition-colors ${filter === t ? "bg-primary text-white" : "text-zinc-400 hover:text-white border border-zinc-800"}`;

  return (
    <div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white flex items-center gap-3">
        <ShieldCheck className="w-9 h-9 text-primary" /> Duyệt báo cáo
      </h1>
      <p className="text-zinc-500 mt-2 mb-6">Kiểm duyệt báo cáo trước khi công khai lên danh sách đen.</p>

      <div className="flex gap-2 mb-6">
        <button data-testid="rfilter-pending" onClick={() => setFilter("pending")} className={tabCls("pending")}>Chờ duyệt</button>
        <button data-testid="rfilter-approved" onClick={() => setFilter("approved")} className={tabCls("approved")}>Đã duyệt</button>
        <button data-testid="rfilter-rejected" onClick={() => setFilter("rejected")} className={tabCls("rejected")}>Từ chối</button>
      </div>

      {loading ? (
        <p className="text-zinc-600 py-10 text-center">Đang tải...</p>
      ) : rows.length === 0 ? (
        <p className="text-zinc-600 py-10 text-center">Không có báo cáo nào.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="bg-card border border-zinc-800 rounded-sm p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-display font-bold text-white text-xl">{r.customer_name}</div>
                  <div className="text-sm text-zinc-500 mt-1">
                    Nhà xe: {r.reported_by_name} · {new Date(r.report_date).toLocaleString("vi-VN")}
                  </div>
                </div>
                <WarningBadge level={r.warning_level} />
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
                <div className="flex justify-between border-b border-zinc-800/50 py-1"><span className="text-zinc-500">SĐT</span><span className="text-zinc-300">{r.phone || "—"}</span></div>
                <div className="flex justify-between border-b border-zinc-800/50 py-1"><span className="text-zinc-500">Ngày sinh</span><span className="text-zinc-300">{r.dob || "—"}</span></div>
                <div className="flex justify-between border-b border-zinc-800/50 py-1"><span className="text-zinc-500">CCCD</span><span className="text-zinc-300 font-mono">{r.cccd_number || "—"}</span></div>
                <div className="flex justify-between border-b border-zinc-800/50 py-1"><span className="text-zinc-500">Bằng lái</span><span className="text-zinc-300">{r.license_number || "—"}</span></div>
                <div className="flex justify-between border-b border-zinc-800/50 py-1"><span className="text-zinc-500">Địa chỉ</span><span className="text-zinc-300">{r.address || "—"}</span></div>
              </div>
              <p className="text-sm text-zinc-300 bg-[#0a0a0a] border border-zinc-800 rounded-sm p-3 mb-3">{r.reason}</p>

              {(r.cccd_front || r.cccd_back || r.license_image || (r.related_images || []).length) ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                  <Thumb path={r.cccd_front} label="CCCD trước" />
                  <Thumb path={r.cccd_back} label="CCCD sau" />
                  <Thumb path={r.license_image} label="Bằng lái" />
                  {(r.related_images || []).map((p, i) => <Thumb key={i} path={p} label={`Liên quan ${i + 1}`} />)}
                </div>
              ) : null}

              {filter === "pending" && (
                <div className="flex gap-2">
                  <button data-testid={`approve-report-${r.id}`} onClick={() => act(r.id, "approve")} className="flex items-center gap-1 bg-green-600/90 hover:bg-green-600 text-white rounded-sm px-4 py-2 text-sm font-semibold transition-colors">
                    <Check className="w-4 h-4" /> Duyệt & công khai
                  </button>
                  <button data-testid={`reject-report-${r.id}`} onClick={() => act(r.id, "reject")} className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:text-white rounded-sm px-4 py-2 text-sm transition-colors">
                    <X className="w-4 h-4" /> Từ chối
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
